/**
 * MobileVoyageHeader.js
 *
 * Full-screen floating header for mobile voyage view.
 * Matches the mockup design - dark bg-gray-900 header that starts
 * from the very top of the screen.
 *
 * Only visible on mobile when in voyageFullScreen mode.
 */

import React, { useState, useEffect } from 'react';
import { ArrowLeft, Users, Fuel, Navigation } from 'lucide-react';
import { VOYAGE_STATUS } from '../../../../../config/operationsConfig';

const MobileVoyageHeader = ({
  voyage,
  onBack,
  onMenuClick
}) => {
  // Elapsed time state
  const [elapsedTime, setElapsedTime] = useState('00:00:00');

  useEffect(() => {
    if (!voyage?.departureTime || voyage?.arrivalTime) return;

    const updateElapsed = () => {
      const start = new Date(voyage.departureTime);
      const now = new Date();
      const diff = now - start;

      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);

      setElapsedTime(`${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`);
    };

    updateElapsed();
    const interval = setInterval(updateElapsed, 1000);

    return () => clearInterval(interval);
  }, [voyage?.departureTime, voyage?.arrivalTime]);

  if (!voyage) return null;

  // Calculate POB
  const crewCount = voyage.crewCount || 0;
  const passengerCount = voyage.passengerCount || 0;
  const totalPOB = voyage.personsOnBoard || (crewCount + passengerCount) || 0;

  // Get route info
  const departure = voyage.departure || voyage.from?.name || (typeof voyage.from === 'string' ? voyage.from : 'Unknown');
  const destination = voyage.destination || voyage.to?.name || (typeof voyage.to === 'string' ? voyage.to : 'Unknown');

  // Is voyage underway?
  const isUnderway = voyage.status === VOYAGE_STATUS.UNDERWAY;

  return (
    <div className="bg-gray-900 text-white shadow-md z-50 shrink-0 sticky top-0 pb-4">
      <div className="px-5 py-5 pt-6">
        {/* Top row - Status and Timer */}
        <div className="flex justify-between items-center mb-5">
          {/* Back button + Status */}
          <div className="flex items-center gap-3">
            <button
              onClick={onBack}
              className="p-1 -ml-1 text-slate-400 hover:text-white transition-colors"
              aria-label="Back to Operations"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            {isUnderway && (
              <div className="flex items-center gap-2 text-cyan-400 font-medium">
                <span className="relative flex h-3 w-3">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-cyan-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-3 w-3 bg-cyan-500"></span>
                </span>
                Underway
              </div>
            )}
          </div>

          {/* Elapsed timer */}
          <div className="text-sm text-slate-300 font-mono bg-slate-800/80 px-2 py-1 rounded">
            {elapsedTime}
          </div>
        </div>

        {/* Voyage title and route */}
        <div className="flex justify-between items-end">
          <div>
            <h2 className="text-2xl font-bold mb-1 tracking-tight">
              {voyage.voyageNumber || `Voyage ${voyage.id?.slice(-6) || '---'}`}
            </h2>
            <p className="text-sm text-slate-400">{departure} → {destination}</p>
          </div>
        </div>
      </div>

      {/* Stats Strip */}
      <div className="mx-5 bg-slate-800/80 rounded-xl px-4 py-3 border border-slate-700/50 flex justify-between">
        {/* Speed - placeholder for now */}
        <div className="flex items-center gap-2 text-sm">
          <Navigation className="h-4 w-4 text-emerald-400" />
          <span className="font-medium">--.- kts</span>
        </div>

        <div className="w-px h-4 bg-slate-700"></div>

        {/* POB */}
        <div className="flex items-center gap-2 text-sm">
          <Users className="h-4 w-4 text-cyan-400" />
          <span className="font-medium">{totalPOB} POB</span>
        </div>

        <div className="w-px h-4 bg-slate-700"></div>

        {/* Fuel */}
        <div className="flex items-center gap-2 text-sm">
          <Fuel className={`h-4 w-4 ${(voyage.currentFuel || 0) <= 25 ? 'text-red-400' : 'text-amber-400'}`} />
          <span className="font-medium">{voyage.currentFuel ?? '--'}%</span>
        </div>
      </div>
    </div>
  );
};

export default MobileVoyageHeader;
